import { useState, useEffect, useMemo, useRef } from 'react';
import map from 'lodash/map';

import { alphabetizeBy } from '@ion/mrdash';
import { useUserContext } from '@ion/user';
import {
  // useGetDeployablePipelines, TODO: Swap in Action Proxy method for hook version
  useDeployPipeline,
  useQueryIntegrations,
  useQueryAllPipelineTags,
  useLazyQueryPipeline,
  useGetPipeline,
  useRemovePipelineIntegrations,
} from '@ion/api';
import { useGetDeployablePipelines } from '@ion/ion-experimental'; // TODO: Swap in Action Proxy method for hook version
import { useConcurrentMap, BULK_DEPLOY_CONCURRENCY } from 'ion-experimental/api/concurrency';
import { withTransientRetry } from 'ion-experimental/api/retry';
import { useToggle } from '@ion/components';

import buildPipelineJson from 'app/pipeline-details/PipelineIntegrationsSection/PipelineIntegrations/DeployModal/buildPipelineJson';
import buildRevisionOptions from 'app/shared/playbooks/buildRevisionOptions';
import resolvePipelineConfiguration from 'app/shared/translation/pipelines/resolvePipelineConfiguration';

// "All" options for integration and revision selects
const DEFAULT_INTEGRATION_OPTION = {
  value: '_ALL_',
  label: 'All Integrations',
};
const DEFAULT_REVISION_OPTION = {
  value: '_ALL_',
  label: 'All Revisions',
};

/**
 * Companion hook for BulkDeployPageContext that manages all state and logic
 */
export default function useBulkDeployPageContextProps() {
  const { user } = useUserContext();

  // Filter state
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(DEFAULT_INTEGRATION_OPTION.value);
  const [selectedRevision, setSelectedRevision] = useState(DEFAULT_REVISION_OPTION.value);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // AbortController ref for canceling in-flight requests
  const abortControllerRef = useRef(null);

  // Data state
  const [erroredPipelines, setErroredPipelines] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // UI state
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState(new Set());
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployResults, setDeployResults] = useState([]);
  const [clearSelectedKey, toggleClearSelectedKey] = useToggle();
  const [onlyShowSelected, setOnlyShowSelected] = useState(false);

  // Diff modal state - stores the pipeline to show diff for (null when modal is closed)
  const [diffModalPipeline, setDiffModalPipeline] = useState(null);

  // API hooks
  const [getDeployablePipelines, { progress: reconcileProgress }] = useGetDeployablePipelines();
  const [deployPipeline] = useDeployPipeline();
  const [getPipeline] = useGetPipeline();
  const [queryPipeline] = useLazyQueryPipeline();
  const [removePipelineIntegrations] = useRemovePipelineIntegrations({ successMsg: null });
  const [runDeployments, deployProgress] = useConcurrentMap();
  const {
    data: integrations = [],
    loading: integrationsLoading,
    error: integrationsError,
  } = useQueryIntegrations({
    variables: { limit: null },
  });

  // Fetch tags for filter dropdown
  const { data: allTags, loading: tagsLoading } = useQueryAllPipelineTags();
  const tagOptions = (allTags ?? []).map(tag => ({ value: tag.id, label: tag.label }));

  // Build integration options (memoized) - includes "All Integrations" option
  const integrationOptions = useMemo(() => {
    const options = integrations.map(({ id, name }) => ({ value: id, label: name }));

    return [DEFAULT_INTEGRATION_OPTION, ...(options.length ? alphabetizeBy(options, 'label') : [])];
  }, [integrations]);

  // Build revision options by integration ID (memoized) - includes "All Revisions" option.
  // Disabled revisions stay selectable — this select filters pipelines, it doesn't pick a target.
  const revisionsByIntegrationId = useMemo(() => {
    const revisionsMap = integrations.reduce((map, integration) => {
      map[integration.id] = [
        DEFAULT_REVISION_OPTION,
        ...buildRevisionOptions(integration.playbooks, { blockDisabled: false }),
      ];
      return map;
    }, {});

    // Add "All Revisions" option for the "_ALL_" integration key
    revisionsMap[DEFAULT_INTEGRATION_OPTION.value] = [DEFAULT_REVISION_OPTION];

    return revisionsMap;
  }, [integrations]);

  // Integration select disabled message
  const integrationSelectDisabled = integrationsLoading || Boolean(integrationsError) || !integrations.length;
  const integrationSelectDisabledMessage = useMemo(() => {
    if (!integrationSelectDisabled) return null;
    if (integrationsLoading) return 'Loading integrations...';
    if (integrationsError) return 'Error loading integrations';
    return 'No integrations configured';
  }, [integrationSelectDisabled, integrationsLoading, integrationsError]);

  // Revision select disabled message
  const revisionSelectDisabled = integrationsLoading || selectedIntegration === DEFAULT_INTEGRATION_OPTION.value;
  const revisionSelectDisabledMessage = useMemo(() => {
    if (!revisionSelectDisabled) return null;
    if (integrationsLoading) return 'Loading revisions...';
    if (integrationsError) return 'Error loading integrations';
    if (selectedIntegration === DEFAULT_INTEGRATION_OPTION.value) return 'Select an integration';
    return 'No revisions configured';
  }, [revisionSelectDisabled, integrationsLoading, integrationsError, selectedIntegration]);

  const handleIntegrationChange = integrationSelection => {
    const integrationId = integrationSelection?.value;
    setSelectedIntegration(integrationId);

    // Reset revision to "All Revisions" when integration changes
    setSelectedRevision(DEFAULT_REVISION_OPTION.value);
  };

  const handleRevisionChange = revisionSelection => {
    const revisionId = revisionSelection?.value;
    setSelectedRevision(revisionId);
  };

  // Get pipelines based on filters
  const handleGetPipelines = async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoadingPipelines(true);
    setPipelines([]);
    setTotalCount(0);
    setOnlyShowSelected(false);
    setSelectedPipelineIds(new Set());
    toggleClearSelectedKey();

    try {
      // Convert "_ALL_" sentinel values to undefined for the API
      const integrationId = selectedIntegration === DEFAULT_INTEGRATION_OPTION.value ? undefined : selectedIntegration;
      const playbookId = selectedRevision === DEFAULT_REVISION_OPTION.value ? undefined : selectedRevision;

      const response = await getDeployablePipelines({
        clusterIds: selectedClusters,
        integrationId,
        playbookId,
        tagIds: selectedTagIds,
        searchTerm: searchTerm || undefined,
      });

      // Check if this request was aborted before processing the response
      if (abortController.signal.aborted) {
        return;
      }

      const data = response?.data?.get_deployable_pipelines;

      const erroredPipelines = (data?.pipelines || []).filter(pipeline => pipeline.status === 'Error');
      const healthyPipelines = (data?.pipelines || []).filter(pipeline => pipeline.status === 'Staged for Deploy');

      // Add deploymentStatus to each pipeline object
      const pipelinesWithDeploymentStatus = healthyPipelines.map(pipeline => ({
        ...pipeline,
        deploymentStatus: { status: 'idle', error: null },
      }));

      setErroredPipelines(erroredPipelines);
      setPipelines(pipelinesWithDeploymentStatus);
      setTotalCount(data?.totalCount || 0);
    } catch (error) {
      // Don't log errors for aborted requests
      if (error.name !== 'AbortError') {
        console.error('Error fetching deployable pipelines:', error);
      }

      // Only clear state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setPipelines([]);
        setTotalCount(0);
      }
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoadingPipelines(false);
      }
    }
  };

  // Selection management
  const handleSelectPipeline = (pipelineId, selected) => {
    setSelectedPipelineIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(pipelineId);
      } else {
        next.delete(pipelineId);
      }
      return next;
    });
  };

  const handleSelectAll = selected => {
    if (selected) {
      setSelectedPipelineIds(new Set(pipelines.map(p => p.id)));
    } else {
      setSelectedPipelineIds(new Set());
    }
  };

  const onClearSelected = () => {
    toggleClearSelectedKey();
    setSelectedPipelineIds(new Set());
  };

  const onSort = ({ sortField }, sortDirection) => {
    const sortedPipelines = [...pipelines].sort((a, b) => {
      let comparatorA, comparatorB;

      // Handle different field types
      switch (sortField) {
        case 'lastDeployed':
          comparatorA = new Date(a.lastDeployed).getTime();
          comparatorB = new Date(b.lastDeployed).getTime();
          break;
        case 'name':
          comparatorA = a.name.toLowerCase();
          comparatorB = b.name.toLowerCase();
          break;
        case 'writeKey':
          comparatorA = a.writeKey.toLowerCase();
          comparatorB = b.writeKey.toLowerCase();
          break;
        case 'cluster':
          comparatorA = a.cluster.name.toLowerCase();
          comparatorB = b.cluster.name.toLowerCase();
          break;
        default:
          return 0;
      }

      if (comparatorA === comparatorB) return 0;

      const comparison = comparatorA < comparatorB ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setPipelines(sortedPipelines);
  };

  // Helper to update a pipeline's deployment status
  const updatePipelineStatus = (pipelineId, status, error = null) => {
    setPipelines(prev => prev.map(p => (p.id === pipelineId ? { ...p, deploymentStatus: { status, error } } : p)));
  };

  // Start deployment
  const handleStartDeployment = async () => {
    const selectedPipelines = pipelines.filter(p => selectedPipelineIds.has(p.id));

    // Reset any prior run's results and set deploying state once before starting
    setDeployResults([]);
    setIsDeploying(true);

    try {
      // Deploy in a concurrency-capped pool so a large selection doesn't swamp the cluster
      const results = await runDeployments(selectedPipelines, BULK_DEPLOY_CONCURRENCY, async pipeline => {
        try {
          // Update status to deploying (combines compile + deploy steps)
          updatePipelineStatus(pipeline.id, 'deploying');

          // Query for entire staged pipeline
          const { data, error } = await queryPipeline({ variables: { pipelineId: pipeline.id } });

          if (error) {
            throw error;
          }

          const { clusterId, writekey, pipelineIntegrations } = data.pipelines_by_pk;

          // Fetch entire deployed pipeline
          const deployedPipelineResponse = await withTransientRetry(() => getPipeline({ clusterId, writekey }));

          // Error out of deployment for this particular pipeline if we get an unexpected response from the cluster
          const deployedPipelineFetchFailed =
            deployedPipelineResponse.errors && !deployedPipelineResponse.errors.message.includes('404');
          if (deployedPipelineFetchFailed) {
            throw new Error(deployedPipelineResponse.errors.message);
          }

          const ionIntegrationIds = new Set(map(pipelineIntegrations, 'integrationId'));
          const deployedRevisions = deployedPipelineResponse.pipeline?.integrations ?? [];
          const cliManagedIntegrations = deployedRevisions.reduce((unmanagedIntegrations, deployedIntegration) => {
            const isCliManaged = !ionIntegrationIds.has(deployedIntegration.id);

            return isCliManaged ? [...unmanagedIntegrations, deployedIntegration] : unmanagedIntegrations;
          }, []);

          // Ion does not own every pipeline-level feature yet, so redeploys must retain the cluster's configuration.
          const compiledPipeline = buildPipelineJson({
            pipelineIntegrations,
            cliManagedIntegrations,
            author: user.email,
            writeKey: writekey,
            pipelineConfiguration: resolvePipelineConfiguration({
              deployedPipeline: deployedPipelineResponse.pipeline,
              configuredPipeline: data.pipelines_by_pk,
            }),
          });

          // Deploy pipeline
          const pipelineDeployResponse = await withTransientRetry(() =>
            deployPipeline({
              id: pipeline.id,
              name: pipeline.name,
              clusterId,
              writekey,
              pipelineJSON: compiledPipeline,
            })
          );

          if (pipelineDeployResponse.errors) {
            throw new Error(pipelineDeployResponse.errors.message);
          }

          // Remove integrations staged to be deleted from the pipeline
          const integrationsToRemove = pipelineIntegrations.filter(
            pipelineIntegration => pipelineIntegration.stagedRevision.stagedForDelete
          );
          removePipelineIntegrations(integrationsToRemove);

          // Update status to success
          updatePipelineStatus(pipeline.id, 'success');

          return { pipelineId: pipeline.id, name: pipeline.name, success: true };
        } catch (error) {
          updatePipelineStatus(pipeline.id, 'error', error.message || 'Deployment failed');

          return { pipelineId: pipeline.id, name: pipeline.name, success: false, error: error.message };
        }
      });

      setDeployResults(results);
    } finally {
      // Clear deploying state once after all complete
      setIsDeploying(false);
    }
  };

  // Fetch pipelines on mount
  useEffect(() => {
    handleGetPipelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser navigation warning during deployment
  useEffect(() => {
    if (!isDeploying) {
      onClearSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeploying]);

  return {
    // Filter state
    selectedClusters,
    selectedIntegration,
    selectedRevision,
    searchTerm,

    selectedTagIds,

    // Filter methods
    setSelectedClusters,
    handleIntegrationChange,
    handleRevisionChange,
    setSearchTerm,
    setSelectedTagIds,

    // Integration/Revision select data
    integrationOptions,
    revisionsByIntegrationId,
    integrationSelectDisabled,
    integrationSelectDisabledMessage,
    revisionSelectDisabled,
    revisionSelectDisabledMessage,
    tagOptions,
    tagsLoading,

    // Data
    erroredPipelines,
    pipelines,
    totalCount,
    loadingPipelines,
    reconcileProgress,
    deployProgress,
    deployResults,

    // UI state
    selectedPipelineIds,
    deployModalOpen,
    setDeployModalOpen,
    isDeploying,
    clearSelectedKey,
    onSort,
    onlyShowSelected,
    setOnlyShowSelected,

    // Data methods
    handleGetPipelines,

    // Selection methods
    handleSelectPipeline,
    handleSelectAll,
    setSelectedPipelineIds,

    // Deployment flow
    handleStartDeployment,

    // Diff modal
    diffModalPipeline,
    setDiffModalPipeline,
  };
}
